import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Popover,
	Stack,
	TextField,
	Tooltip,
	Typography,
	createFilterOptions,
	useTheme,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";

import {
	atualizarSlot,
	carregarSlots,
	criarSlot,
	excluirSlot,
	getSnapshot,
	subscribe,
} from "./slotsCache";
import stylesFn from "./styles";

const filtrar = createFilterOptions({
	stringify: (opcao) => `${opcao.key || ""} ${opcao.label || ""}`,
});

const ABA_GLOBAL = "*";

// Seletor do slot de métrica: busca no catálogo, cria com "Criar", renomeia e
// exclui inline. `mostrarTodasAbas` é controlado pelo card da aba, não por linha.
export default function SlotMetricaSelector({
	value = "",
	onChange,
	aba,
	notify,
	disabled = false,
	mostrarTodasAbas = false,
}) {
	const theme = useTheme();
	const styles = useMemo(() => stylesFn(theme), [theme]);

	const [snapshot, setSnapshot] = useState(getSnapshot);
	const [salvando, setSalvando] = useState(false);
	// popover ancorado neste container, não no botão de lápis (que é
	// desmontado junto com a opção quando o Autocomplete fecha)
	const containerRef = useRef(null);
	const [editando, setEditando] = useState(null); // { slot }

	useEffect(() => {
		const cancelar = subscribe(() => setSnapshot(getSnapshot()));
		carregarSlots().catch(() => {
			notify?.({
				children: "Error: Não foi possível carregar as métricas",
				severity: "error",
			});
		});
		return cancelar;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const catalogo = snapshot.slots || [];

	const opcoes = useMemo(() => {
		const noEscopo = catalogo.filter(
			(slot) => slot.aba === ABA_GLOBAL || slot.aba === aba,
		);
		const base = mostrarTodasAbas ? catalogo : noEscopo;

		const ordenadas = [...base].sort((a, b) => {
			if (a.aba === b.aba) return a.key.localeCompare(b.key);
			if (a.aba === ABA_GLOBAL) return -1;
			if (b.aba === ABA_GLOBAL) return 1;
			return a.aba.localeCompare(b.aba);
		});

		// chave legada fora do catálogo: entra como opção pra não sumir ao salvar
		if (value && !catalogo.some((slot) => slot.key === value)) {
			return [
				{
					id: `__livre-${value}`,
					key: value,
					label: value,
					foraDoCatalogo: true,
				},
				...ordenadas,
			];
		}
		return ordenadas;
	}, [catalogo, aba, mostrarTodasAbas, value]);

	const selecionado = useMemo(
		() => opcoes.find((slot) => slot.key === value) || null,
		[opcoes, value],
	);

	const handleCriar = useCallback(
		async (texto) => {
			const digitado = String(texto || "").trim();
			if (!digitado) return;

			setSalvando(true);
			try {
				const pareceChave = /^[a-z][a-zA-Z0-9]*$/.test(digitado);
				const resultado = await criarSlot({
					key: pareceChave ? digitado : undefined,
					label: digitado,
					aba: aba || null,
				});

				if (!resultado?.slot) return;
				onChange?.(resultado.slot.key);

				if (!resultado.created) {
					notify?.({
						children: `Métrica "${resultado.slot.key}" já existia e foi reaproveitada`,
						severity: "info",
					});
				} else if (!pareceChave) {
					notify?.({
						children: `Métrica criada com a chave "${resultado.slot.key}"`,
						severity: "success",
					});
				}
			} catch (e) {
				notify?.({
					children:
						e?.response?.data?.error ||
						"Error: Não foi possível criar a métrica",
					severity: "error",
				});
			} finally {
				setSalvando(false);
			}
		},
		[aba, onChange, notify],
	);

	return (
		<Box ref={containerRef} sx={{ width: "100%" }}>
			<Autocomplete
				fullWidth
				size="small"
				disabled={disabled || salvando}
				loading={snapshot.carregando}
				value={selecionado}
				options={opcoes}
				filterOptions={(opts, params) => {
					const filtradas = filtrar(opts, params);
					const digitado = params.inputValue.trim();
					const existe = opts.some(
						(slot) => slot.key.toLowerCase() === digitado.toLowerCase(),
					);
					if (digitado && !existe) {
						filtradas.push({ id: "__criar", criar: true, inputValue: digitado });
					}
					return filtradas;
				}}
				getOptionLabel={(opcao) =>
					opcao?.criar ? opcao.inputValue : opcao?.key || ""
				}
				isOptionEqualToValue={(opcao, valor) => opcao.key === valor.key}
				onChange={(_event, novo) => {
					if (!novo) {
						onChange?.("");
						return;
					}
					if (novo.criar) {
						handleCriar(novo.inputValue);
						return;
					}
					onChange?.(novo.key);
				}}
				renderOption={(props, opcao) => {
					if (opcao.criar) {
						return (
							<Box component="li" {...props} key="__criar">
								<Typography variant="body2" color="primary">
									Criar &quot;{opcao.inputValue}&quot;
								</Typography>
							</Box>
						);
					}

					return (
						<Box component="li" {...props} key={opcao.id} sx={styles.option}>
							<Stack sx={styles.optionTexts}>
								<Stack direction="row" alignItems="center" gap={0.5}>
									<Typography sx={styles.optionKey}>{opcao.key}</Typography>
									{opcao.aba === ABA_GLOBAL && (
										<Tooltip title="Métrica global: disponível em qualquer aba">
											<PublicOutlined sx={styles.globalIcon} />
										</Tooltip>
									)}
									{opcao.foraDoCatalogo && (
										<Chip
											size="small"
											label="fora do catálogo"
											sx={styles.chipAviso}
										/>
									)}
								</Stack>
								{opcao.label && opcao.label !== opcao.key && (
									<Typography sx={styles.optionLabel}>{opcao.label}</Typography>
								)}
							</Stack>

							{!opcao.foraDoCatalogo && (
								<IconButton
									size="small"
									onMouseDown={(e) => {
										e.stopPropagation();
										e.preventDefault();
									}}
									onClick={(e) => {
										e.stopPropagation();
										setEditando({ slot: opcao });
									}}
								>
									<EditOutlined fontSize="inherit" />
								</IconButton>
							)}
						</Box>
					);
				}}
				noOptionsText="Digite para criar uma métrica"
				renderInput={(params) => (
					<TextField
						{...params}
						variant="standard"
						placeholder="escolher métrica"
						InputProps={{
							...params.InputProps,
							disableUnderline: true,
							sx: styles.inputMono,
							endAdornment: (
								<>
									{salvando ? <CircularProgress size={14} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
			/>

			<SlotEditPopover
				estado={editando}
				anchorEl={containerRef.current}
				onClose={() => setEditando(null)}
				notify={notify}
				onExcluido={(chaveExcluida) => {
					if (chaveExcluida === value) onChange?.("");
				}}
			/>
		</Box>
	);
}

// painel de renomear/excluir; a chave não aparece aqui, só o backend permite
// alterá-la, e só com uso zero
function SlotEditPopover({ estado, anchorEl, onClose, notify, onExcluido }) {
	const theme = useTheme();
	const styles = useMemo(() => stylesFn(theme), [theme]);

	const slot = estado?.slot;
	const [label, setLabel] = useState("");
	const [descricao, setDescricao] = useState("");
	const [ocupado, setOcupado] = useState(false);
	const [conflito, setConflito] = useState(null);

	useEffect(() => {
		setLabel(slot?.label || "");
		setDescricao(slot?.descricao || "");
		setConflito(null);
	}, [slot]);

	if (!slot) return null;

	const salvar = async () => {
		setOcupado(true);
		try {
			await atualizarSlot({ id: slot.id, label, descricao });
			notify?.({ children: "Métrica atualizada", severity: "success" });
			onClose();
		} catch (e) {
			notify?.({
				children:
					e?.response?.data?.error || "Error: Não foi possível atualizar a métrica",
				severity: "error",
			});
		} finally {
			setOcupado(false);
		}
	};

	const excluir = async (force) => {
		setOcupado(true);
		try {
			await excluirSlot({ id: slot.id, force });
			notify?.({ children: "Métrica removida do catálogo", severity: "success" });
			onExcluido?.(slot.key);
			onClose();
		} catch (e) {
			const dados = e?.response?.data;
			if (e?.response?.status === 409) {
				setConflito(dados);
			} else {
				notify?.({
					children: dados?.error || "Error: Não foi possível excluir a métrica",
					severity: "error",
				});
			}
		} finally {
			setOcupado(false);
		}
	};

	return (
		<Popover
			open
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			slotProps={{ paper: { sx: styles.editPaper } }}
		>
			<Typography sx={styles.editKey}>{slot.key}</Typography>
			<Typography variant="caption" color="text.secondary">
				A chave é fixa porque o app do cliente casa por ela.
			</Typography>

			<TextField
				variant="standard"
				label="Rótulo"
				value={label}
				onChange={(e) => setLabel(e.target.value)}
				disabled={ocupado}
				fullWidth
			/>
			<TextField
				variant="standard"
				label="Descrição"
				value={descricao}
				onChange={(e) => setDescricao(e.target.value)}
				disabled={ocupado}
				fullWidth
				multiline
				maxRows={3}
			/>

			{conflito && (
				<Alert severity="warning" sx={styles.alertConflito}>
					{conflito.error}
					<Box component="ul" sx={styles.listaModelos}>
						{(conflito.modelos || []).slice(0, 8).map((m) => (
							<li key={m.modeloId}>
								{[m.fabricante, m.funcao, m.modelo].filter(Boolean).join(" · ")}
							</li>
						))}
						{(conflito.modelos || []).length > 8 && (
							<li>…e mais {conflito.modelos.length - 8}</li>
						)}
					</Box>
					<Typography variant="caption" display="block">
						Remover do catálogo não altera nenhum dashboard salvo: os modelos
						acima continuam funcionando. O slot só deixa de ser sugerido.
					</Typography>
					<Button
						size="small"
						color="warning"
						disabled={ocupado}
						onClick={() => excluir(true)}
					>
						Remover do catálogo mesmo assim
					</Button>
				</Alert>
			)}

			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Button
					size="small"
					color="error"
					disabled={ocupado}
					onClick={() => excluir(false)}
					startIcon={<DeleteOutline fontSize="small" />}
				>
					Excluir
				</Button>
				<Stack direction="row" gap={1}>
					<Button size="small" onClick={onClose} disabled={ocupado}>
						Cancelar
					</Button>
					<Button
						size="small"
						variant="contained"
						onClick={salvar}
						disabled={ocupado || !label.trim()}
					>
						{ocupado ? <CircularProgress size={16} /> : "Salvar"}
					</Button>
				</Stack>
			</Stack>
		</Popover>
	);
}
